import { Text } from '#app/components/Primitives/Text.js';
import { ProBadge } from '#app/components/ProBadge/ProBadge.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useMatchMedia } from '#app/hooks/useMatchMedia.js';
import { useOnElementViewed } from '#app/hooks/useOnElementViewed.js';
import { parseMediaQueryForMatchMedia } from '#app/utilities/parseMediaQueryForMatchMedia.js';
import { SlimBarChartIcon } from './MobileDiscoveryInsightsButton.js';
import { getRoute } from '@contra/routes';
import { useCallback } from 'react';
import { css, styled } from 'styled-components';

const StyledButtonLink = styled.a`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-height: 36px;
  padding: 8px 10px 8px 10px;
  white-space: nowrap;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colorsV2.gray30};

  &:hover {
    background-color: ${({ theme }) => theme.colorsV2.gray05};
  }

  transition: background-color 0.2s ease-out;
`;

const Gray80Text = styled(Text)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.gray80};
    font-weight: 600;
    line-height: 16px;

    ${theme.mediaQueries.xl} {
      display: block;
    }
  `}
`;

export const DiscoveryInsightsButton: React.FC = () => {
  const { track } = useAnalytics();
  const currentUser = useCurrentUser();

  const hasContraPro =
    currentUser?.profile?.hasActivePortfolioSubscription ?? false;

  const analyticsSectionSuffix = hasContraPro ? 'subscribed' : 'unsubscribed';

  const handleTrackView = useCallback(() => {
    track('button', {
      action: 'viewed',
      button_name: 'view_discovery_score',
      section: `profile_${analyticsSectionSuffix}`,
    });
  }, [analyticsSectionSuffix, track]);

  const handleCtaClick = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'view_discovery_score',
      section: `profile_${analyticsSectionSuffix}`,
    });
  }, [analyticsSectionSuffix, track]);

  const ref = useOnElementViewed<HTMLAnchorElement>(handleTrackView);

  const isFullText = useMatchMedia(
    parseMediaQueryForMatchMedia(`@media screen and (min-width: 1600px)`),
  );

  return (
    <StyledButtonLink
      href={getRoute('/independent/discovery')}
      onClick={handleCtaClick}
      ref={ref}
      target="_blank"
    >
      <SlimBarChartIcon />
      <Gray80Text
        as="span"
        textStyle="subtitleSmall"
      >
        {isFullText ? `View profile insights` : `View insights`}
      </Gray80Text>{' '}
      <ProBadge size="xs" />
    </StyledButtonLink>
  );
};
